/**
 * Change Security Phrase Page
 * Accessed via email link with token
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getFailoverAwareAuth } from '../../config/firebaseWithFailover';
import { securityPhraseService } from '../../services/securityPhraseService';
import Logger from '../../services/logger';
import logoImg from '../../assets/logo.png';
import styles from './Login.module.css';

const ChangeSecurityPhrase: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const resetPassword = searchParams.get('resetPassword') === 'true';
  
  const [step, setStep] = useState<'verify' | 'setPhrase' | 'success'>('verify');
  const [userId, setUserId] = useState<string | null>(null);
  const [phrase, setPhrase] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setError('Invalid link. Please request a new security phrase change link.');
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    if (!token) return;

    setLoading(true);
    setError('');

    try {
      // Use Cloud Function to verify token (works for unauthenticated users)
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../config/firebase');
      const verifyTokenCallable = httpsCallable(functions, 'verifySecurityPhraseChangeToken');
      
      const result = await verifyTokenCallable({ token }) as {
        data: { success: boolean; userId?: string; email?: string; error?: string }
      };
      
      if (result.data.success && result.data.userId) {
        setUserId(result.data.userId);
        setUserEmail(result.data.email || '');
        
        setStep('setPhrase');
      } else {
        setError(result.data.error || 'Invalid or expired token. Please request a new link.');
      }
    } catch (error: any) {
      Logger.error('[ChangeSecurityPhrase] Error verifying token:', error as Error);
      setError(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPhrase = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!userId || !token) {
      setError('Session expired. Please request a new link.');
      return;
    }

    // Validate format
    const formatValidation = securityPhraseService.validatePhraseFormat(phrase);
    if (!formatValidation.valid) {
      setError(formatValidation.error || 'Invalid format');
      return;
    }

    // Check if phrases match
    if (phrase !== confirmPhrase) {
      setError('Security phrases do not match');
      return;
    }

    setLoading(true);

    try {
      // Use Cloud Function to change security phrase (works for unauthenticated users)
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../config/firebase');
      const changePhraseCallable = httpsCallable(functions, 'changeSecurityPhraseWithToken');
      
      const result = await changePhraseCallable({
        userId,
        token,
        newPhrase: phrase
      }) as { data: { success: boolean; error?: string } };
      
      if (result.data.success) {
        // If resetPassword flag is set, also send password reset email
        if (resetPassword && userEmail) {
          try {
            const auth = getFailoverAwareAuth();
            await sendPasswordResetEmail(auth, userEmail);
            Logger.info('[ChangeSecurityPhrase] Password reset email sent', { userId, email: userEmail });
          } catch (emailError: any) {
            Logger.error('[ChangeSecurityPhrase] Error sending password reset email:', emailError as Error);
            // Don't fail the whole flow if password reset email fails
          }
        }
        
        setStep('success');
      } else {
        setError(result.data.error || 'Failed to set security phrase');
      }
    } catch (error) {
      Logger.error('[ChangeSecurityPhrase] Error setting security phrase:', error as Error);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'verify' && loading) {
    return (
      <div className={styles.loginContainer}>
        <div className={styles.loginBox}>
          <h1>Verifying Link...</h1>
          <p>Please wait while we verify your security phrase change link.</p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <>
        <Helmet>
          <title>Security Phrase Changed - Tranzit.io</title>
        </Helmet>
        <div className={styles.loginContainer}>
          <div className={styles.loginBox}>
            <h1>Success!</h1>
            <div style={{ color: '#28a745', marginBottom: '16px' }}>
              <p>Your security phrase has been changed successfully.</p>
              {resetPassword && (
                <p>Password reset email has been sent to your email address.</p>
              )}
            </div>
            <Link to="/login" className={styles.loginButton} style={{ display: 'inline-block', textDecoration: 'none' }}>
              Go to Login
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Change Security Phrase - Tranzit.io</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className={styles.loginContainer}>
        <header className={styles.header}>
          <Link to="/" className={styles.logo} style={{ textDecoration: 'none' }}>
            <img src={logoImg} alt="Tranzit.io Logo" className={styles.logoImg} loading="eager" width="200" height="50" />
            <div className={styles.textLogo}>
              <span>T</span>
              <span>R</span>
              <span>A</span>
              <span>N</span>
              <span>Z</span>
              <span>I</span>
              <span>T</span>
            </div>
          </Link>
          <nav className={styles.nav}>
            <Link className={styles.menuLink} to="/">Home</Link>
            <Link className={styles.menuLink} to="/login">Login</Link>
          </nav>
        </header>
        
        <div className={styles.loginContent}>
          <div className={styles.loginBox}>
            <h1>Change Security Phrase</h1>
            
            {error && <div className={styles.error}>{error}</div>}
            
            {step === 'setPhrase' && (
              <form onSubmit={handleSetPhrase} className={styles.form}>
                {resetPassword && (
                  <div style={{ marginBottom: '16px', padding: '12px', background: '#d1ecf1', borderRadius: '4px' }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#0c5460' }}>
                      <strong>Note:</strong> After setting your security phrase, a password reset email will be sent to your inbox.
                    </p>
                  </div>
                )}
                
                <div style={{ marginBottom: '16px', padding: '12px', background: '#fff3cd', borderRadius: '4px' }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#856404' }}>
                    <strong>Important:</strong> Choose 3 words you'll remember (e.g., "Red Green Blue"). 
                    Save this phrase securely - you'll need it to reset your password. After submitting, 
                    the phrase will be concealed and cannot be viewed again.
                  </p>
                </div>
                
                <div className={styles.inputGroup}>
                  <label htmlFor="phrase">Enter 3-Word Security Phrase</label>
                  <input
                    type="text"
                    id="phrase"
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    className={styles.input}
                    placeholder="e.g., Red Green Blue"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className={styles.inputGroup}>
                  <label htmlFor="confirmPhrase">Confirm 3-Word Security Phrase</label>
                  <input
                    type="text"
                    id="confirmPhrase"
                    value={confirmPhrase}
                    onChange={(e) => setConfirmPhrase(e.target.value)}
                    className={styles.input}
                    placeholder="Re-enter your phrase"
                    required
                    disabled={loading}
                  />
                </div>
                
                <button type="submit" className={styles.loginButton} disabled={loading}>
                  {loading ? 'Setting...' : resetPassword ? 'Set Phrase & Reset Password' : 'Set Security Phrase'}
                </button>
                
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <Link to="/login" style={{ color: '#007bff', textDecoration: 'none' }}>
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangeSecurityPhrase;
